import { t } from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { useUpdateBillMutation, BillCategoryType, BillListItemFragment, ProductionAssignedInput, useCustomerQuery, useCreateBillMutation } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { PosPasserbyBillsListRow } from '../bill/components/bills-list/pos-passerby-bills-types'

export default function usePosCRUD(propertyId: string, onOperationFailed?: () => void) {
  const { id: tenantId } = useTenant()
  const [updateBillMutation, { loading: updateLoading }] = useUpdateBillMutation()

  const { data: dummyCustomerResult, loading: dummyCustomerLoading } = useCustomerQuery({
    variables: {
      tenantId: tenantId,
      dummy: true,
    },
  })
  const [createBillMutation, { loading: creationLoading }] = useCreateBillMutation()

  const showErrorSnackBar = useErrorSnackBar()
  const showSuccessSnackBar = useSuccessSnackBar()
  const [loading, setLoading] = useState(false)

  const editBill = useCallback(
    async (bill: BillListItemFragment, rows: PosPasserbyBillsListRow[], onOperationCompleted: (updatedBill: BillListItemFragment) => void) => {
      try {
        // New productions pos rows
        const newPosProductionsRows: PosPasserbyBillsListRow[] = []
        // Modified production pos rows
        const updPosProductionsRows: PosPasserbyBillsListRow[] = []

        rows.map((r) => {
          if (bill.productions?.find((p) => p.production.id === r.productionId)) {
            updPosProductionsRows.push(r)
          } else {
            newPosProductionsRows.push(r)
          }
        })

        const existingProductionsId = rows.map((r) => r.productionId)

        const isPasserByBill = bill.reservedResourceId ? false : true

        // 1 - Productions of manual type are updated if needed.
        // In bills related to reservation, the NON manual (automatic) productions is not visible in POS but we need to include that in update mutation.
        // Passer-by bills, instead shows all productions. Therefore, you should not include productions with flag manual 'true' (automatic)
        const updatedProductions: ProductionAssignedInput[] =
          bill.productions
            ?.filter((prod) => (!isPasserByBill && prod.production.manual === false) || existingProductionsId.includes(prod.production.id))
            ?.map((productionAssigned) => {
              const p = productionAssigned.production

              let pUpd: PosPasserbyBillsListRow | undefined = undefined
              if (p.manual) {
                pUpd = updPosProductionsRows.find((productionRow) => productionRow.productionId === p.id)

                const quota = productionAssigned.quota || 1
                const amount = Math.round(pUpd?.amount ? pUpd.amount / quota : p.amount / quota)
                const total = Math.round(pUpd?.total ? pUpd?.total / quota : p.total / quota)

                return {
                  production: {
                    date: pUpd?.competenceDate ?? p.date,
                    chargeCategoryId: pUpd?.chargeCategoryId ?? p.chargeCategoryId,
                    currency: pUpd?.currency ?? p.currency,
                    quantity: pUpd?.quantity ?? p.quantity,
                    amount: amount,
                    total: total,
                    manual: p.manual,
                    vatId: pUpd?.vatId ?? p.vatId,
                    description: p.description,
                    reservedResourceId: p.reservedResourceId || bill.reservedResourceId?.[0],
                    cityTax: { guestId: p.cityTax?.guestId, exoneration: p.cityTax?.exoneration },
                  },
                  productionId: p.id,
                }
              } else {
                const amount = p.amount // productionAssigned.quota
                const total = p.total // productionAssigned.quota

                return {
                  production: {
                    date: p.date,
                    chargeCategoryId: p.chargeCategoryId,
                    currency: p.currency,
                    quantity: p.quantity,
                    amount: amount,
                    total: total,
                    manual: p.manual,
                    vatId: p.vatId,
                    description: p.description,
                    reservedResourceId: p.reservedResourceId || bill.reservedResourceId?.[0],
                    cityTax: { guestId: p.cityTax?.guestId, exoneration: p.cityTax?.exoneration },
                  },
                  productionId: p.id,
                }
              }
            }) ?? []

        // 2 - new manual productions are appended to the list.
        newPosProductionsRows.forEach((row) => {
          updatedProductions.push({
            productionId: '',
            production: {
              date: row.competenceDate,
              chargeCategoryId: row.chargeCategoryId,
              currency: row.currency,
              quantity: row.quantity,
              amount: row.amount,
              total: row.total,
              manual: true,
              vatId: row.vatId,
              description: row.detail,
              reservedResourceId: bill.reservedResourceId?.[0] ?? bill.reservation?.[0]?.reservedResources?.[0].id,
            },
          })
        })

        const result = await updateBillMutation({
          variables: {
            updateBillId: bill.id,
            value: {
              tenantId: tenantId,
              propertyId: propertyId,
              ownerId: bill.ownerId,
              category: bill.category ?? BillCategoryType.EXTRA,
              description: bill.description,
              productions: updatedProductions,
            },
          },
        })
        setLoading(false)

        const updatedBill = result.data?.updateBill
        if (updatedBill === undefined) {
          showErrorSnackBar(result.errors)
        } else {
          showSuccessSnackBar(t('pms.booking.passerby-bill.creation-dialog.update_success'))
          onOperationCompleted(updatedBill)
        }
      } catch (e) {
        setLoading(false)
        showErrorSnackBar(e)
        if (onOperationFailed) onOperationFailed()
      }
    },
    [updateBillMutation, tenantId, propertyId, setLoading, showErrorSnackBar],
  )

  const createDummyBill = useCallback(async () => {
    if (dummyCustomerResult?.customers && dummyCustomerResult.customers.length === 1) {
      const dummyCustomer = dummyCustomerResult.customers[0]
      try {
        const result = await createBillMutation({
          variables: {
            value: {
              tenantId: tenantId,
              propertyId: propertyId,
              ownerId: dummyCustomer.id,
              category: BillCategoryType.EXTRA,
              description: `${dummyCustomer.name}`,
            },
          },
        })
        setLoading(false)

        const newBill = result.data?.createBill as unknown as BillListItemFragment
        if (newBill === undefined) {
          showErrorSnackBar(result.errors)
        } else {
          return newBill
        }
      } catch (e) {
        setLoading(false)
        showErrorSnackBar(e)
      }
    }
  }, [dummyCustomerResult?.customers])

  useEffect(() => {
    setLoading(updateLoading || dummyCustomerLoading || creationLoading)
  }, [updateLoading, dummyCustomerLoading, creationLoading])

  return { editBill, createDummyBill, loading }
}
